import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { apiFetch } from "../lib/api";
import "./../styles/Search.css";
import CampaignPickerModal from "../components/CampaignPickerModal";
import TemplateEditor from "../components/TemplateEditor";
import TemplateEditorModal from "../components/TemplateEditorModal";
import SearchTutorial from "../components/SearchTutorial";
import OutOfCreditsModal from "../components/OutOfCreditsModal.jsx";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

function useDebouncedValue(value, delay = 300) {
  const [val, setVal] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setVal(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return val;
}

async function apiListCampaigns() {
  const j = await apiFetch("/api/email/campaigns", { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiCreateCampaign({ name, template_id, start_at = null, send_window_start = null, send_window_end = null, daily_cap = null }) {
  const j = await apiFetch("/api/email/campaigns", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, template_id, start_at, send_window_start, send_window_end, daily_cap }),
  });
  return j?.id;
}
async function apiAddRecipients(campaignId, recipients) {
  return apiFetch(`/api/email/campaigns/${campaignId}/recipients`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recipients }),
  });
}
async function apiGetRecipients(campaignId, { stage = "", q = "", limit = 50, offset = 0 } = {}) {
  const qs = new URLSearchParams({ stage, q, limit, offset }).toString();
  const j = await apiFetch(`/api/email/campaigns/${campaignId}/recipients?${qs}`, { credentials: "include" });
  return j;
}
async function apiSetCampaignStatus(campaignId, status) {
  return apiFetch(`/api/email/campaigns/${campaignId}/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
}
async function apiBulkStage(ids, stage) {
  return apiFetch(`/api/email/recipients/bulk-stage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, stage }),
  });
}
async function apiForceNextNow(rid) {
  return apiFetch(`/api/email/recipients/${rid}/force-next-now`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
}
async function apiSendTick() {
  return apiFetch(`/api/email/send-tick`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
}

async function fetchReferralCode() {
  try {
    const j = await apiFetch("/api/referral/code");
    setReferralCode(j.code);
  } catch (e) {
    console.error(e);
    alert("Could not get referral code");
  }
}
const DEFAULT_TEMPLATE_NAME = "Default Outreach";
const DEFAULT_TEMPLATE_SUBJECT = "Feature request for {{playlist_name}}";
const DEFAULT_TEMPLATE_HTML = `<p>Hi {{curator_name}},</p><p>I came across your playlist <a href="{{playlist_url}}">{{playlist_name}}</a> and thought this release could be a great fit.</p><p>Best,<br>{{sender_name}}</p>`;
const DEFAULT_TEMPLATE_TEXT = `Hi {{curator_name}}, I came across your playlist {{playlist_name}} ({{playlist_url}}) and thought this release could be a great fit. Best, {{sender_name}}`;

async function apiUpdateTemplate(id, { name, subject, html, text = null }) {
  return apiFetch(`/api/email/templates/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, subject, html, text }),
  });
}

async function refreshTemplatesWithDefault({
  setTplLoading,
  setTplErr,
  setTemplates,
  newCampaignTemplateId,
  setNewCampaignTemplateId,
}) {
  setTplLoading(true);
  setTplErr("");
  try {
    let items = await apiListTemplates();
    let def = items.find(
      (t) => (t.name || "").toLowerCase() === DEFAULT_TEMPLATE_NAME.toLowerCase()
    );
    if (!def) {
      const id = await apiCreateTemplate({
        name: DEFAULT_TEMPLATE_NAME,
        subject: DEFAULT_TEMPLATE_SUBJECT,
        html: DEFAULT_TEMPLATE_HTML,
        text: DEFAULT_TEMPLATE_TEXT,
      });
      def = {
        id,
        name: DEFAULT_TEMPLATE_NAME,
        subject: DEFAULT_TEMPLATE_SUBJECT,
        html: DEFAULT_TEMPLATE_HTML,
        text: DEFAULT_TEMPLATE_TEXT,
      };
      items = [def, ...items];
    } else {
      items = [def, ...items.filter((x) => x.id !== def.id)];
    }
    setTemplates(items);
    if (!newCampaignTemplateId) setNewCampaignTemplateId(def.id);
  } catch (e) {
    setTplErr(e?.message || "Failed to load templates");
  } finally {
    setTplLoading(false);
  }
}

async function apiListTemplates() {
  const j = await apiFetch("/api/email/templates", { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiCreateTemplate({ name, subject, html, text = null }) {
  const j = await apiFetch("/api/email/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, subject, html, text }),
  });
  return j?.id;
}
async function apiGetSteps(campaignId) {
  const j = await apiFetch(`/api/email/campaigns/${campaignId}/steps`, { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiSaveSteps(campaignId, steps) {
  return apiFetch(`/api/email/campaigns/${campaignId}/steps`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ steps }),
  });
}
async function apiScheduleCampaign(campaignId, start_at = null) {
  return apiFetch(`/api/email/campaigns/${campaignId}/schedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(start_at ? { start_at } : {}),
  });
}

const PLAYLIST_BASE = (import.meta.env.VITE_PLAYLIST_BASE || "").replace(/\/+$/, "");
const urlJoin = (base, path) => (base && path ? `${base}${path.startsWith("/") ? "" : "/"}${path}` : path);
const playlistUrl = (p) => (PLAYLIST_BASE ? urlJoin(PLAYLIST_BASE, p) : p);

async function httpJson(url, opts = {}) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} – ${text || res.statusText}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : {};
}

async function fetchFollowersForIds(playlistIds, { maxWorkers = 6, maxAgeHours = 24 * 7 } = {}) {
  if (!playlistIds?.length) return {};
  const res = await fetch("/api/search/followers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids: playlistIds, max_workers: maxWorkers, max_age_hours: maxAgeHours }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} – ${txt || res.statusText}`);
  }
  const j = await res.json();
  return j?.followers || {};
}

function isPaidPlan(sub) {
  if (!sub) return false;
  const status = (sub.status || "").toLowerCase();
  return status === "active" || status === "trialing" || status === "past_due";
}

const ACTIVE_SUB_STATUSES = ["active", "trialing", "past_due"];
const OUTREACH_PLANS = ["outreach"]; // Outreach plan

function hasOutreachPlan(sub) {
  if (!sub) return false;
  const status = (sub.status || "").toLowerCase();
  const name = (sub.name || "").toLowerCase();
  return ACTIVE_SUB_STATUSES.includes(status) && OUTREACH_PLANS.includes(name);
}

function useServerCredits() {
  const [credits, setCredits] = useState(null);
  const [subscription, setSubscription] = useState({ status: "free", name: "Free" });
  const [revealUsage, setRevealUsage] = useState({ used: 0, remaining: 10, limit: 10 });
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const j = await apiFetch("/api/me");
      const c = j?.credits?.balance;
      setCredits(typeof c === "number" || c === null ? c : 0);
      setSubscription(j?.subscription || { status: "free", name: "Free" });

      // Get reveal usage for free users
      if (j?.subscription?.status === "free") {
        try {
          const usage = await apiFetch("/api/usage/reveals");
          setRevealUsage({
            used: usage.used || 0,
            remaining: usage.remaining || 0,
            limit: usage.limit || 10
          });
        } catch {
          setRevealUsage({ used: 0, remaining: 10, limit: 10 });
        }
      }
    } catch {
      setCredits(null);
      setSubscription({ status: "free", name: "Free" });
      setRevealUsage({ used: 0, remaining: 10, limit: 10 });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);
  const spend = async (amount = 1) => {
    for (let i = 0; i < amount; i++) {
      try {
        await apiFetch("/api/usage/reveals/use", { method: "POST" });
      } catch (e) {
        throw e;
      }
    }
    if (subscription?.status === "free") {
      setRevealUsage(prev => ({
        used: (prev.used || 0) + amount,
        remaining: Math.max(0, (prev.remaining || 0) - amount),
        limit: prev.limit || 10
      }));
    }
  };
  return { credits, subscription, revealUsage, loading, refresh, spend };
}

function useRevealedApi() {
  const [items, setItems] = useState([]);
  const [ids, setIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const refresh = async () => {
    setLoading(true);
    setErr("");
    try {
      const j = await apiFetch("/api/revealed");
      const arr = Array.isArray(j.items) ? j.items : [];
      setItems(arr);
      setIds(new Set(arr.map((x) => x.id)));
    } catch (e) {
      setErr(e.message || "Failed to load revealed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);
  const add = async (item) => {
    await apiFetch("/api/revealed", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ playlist_id: item.id, data: item }),
    });
    setItems((prev) => (prev.find((x) => x.id === item.id) ? prev : [{ ...item }, ...prev]));
    setIds((prev) => new Set(prev).add(item.id));
  };
  const remove = async (id) => {
    await apiFetch(`/api/revealed/${encodeURIComponent(id)}`, { method: "DELETE" });
    setItems((p) => p.filter((x) => x.id !== id));
    setIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };
  const clear = async () => {
    await apiFetch("/api/revealed", { method: "DELETE" });
    setItems([]); setIds(new Set());
  };
  const isRevealed = (id) => ids.has(id);
  return { items, ids, isRevealed, add, remove, clear, refresh, loading, err };
}

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-3.5-3.5" />
  </svg>
);
const InfoDot = ({ title, children }) => (
  <span className="psl-help" tabIndex={0}>
    <span className="psl-info" aria-hidden>i</span>
    <div className="psl-tip" role="tooltip">{children ? children : <p>{title}</p>}</div>
  </span>
);

function StatusBadge({ status }) {
  const color =
    status === "sending" ? "#10b981" :
      status === "paused" ? "#f59e0b" :
        status === "scheduled" ? "#3b82f6" : "#6b7280";
  return (
    <span className="psl-badge" style={{ color }}>
      <span className="dot" style={{ background: color }} />
      {status}
    </span>
  );
}

function CampaignHeader({ c, onRefresh, onStartNow, onPause, onSetSending, onSendTick, onSchedule, busy = false }) {
  const total = (c?.queued || 0) + (c?.emailed || 0);
  const pct = total ? Math.min(100, Math.round(((c?.emailed || 0) / total) * 100)) : 0;
  const [when, setWhen] = React.useState("");
  return (
    <div className="psl-camp-head">
      <div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <h4 style={{ margin: 0 }}>{c?.name || "Campaign"}{c?.id ? <span style={{ opacity: .7 }}> #{c.id}</span> : null}</h4>
          {c?.status && <StatusBadge status={c.status} />}
        </div>
        <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13, color: "var(--psl-muted)" }}>
          <span>queued <b>{c?.queued ?? 0}</b></span>
          <span>emailed <b>{c?.emailed ?? 0}</b></span>
          <span>replied <b>{c?.replied ?? 0}</b></span>
          {typeof c?.daily_cap === "number" && <span>daily cap <b>{c.daily_cap}</b></span>}
          {c?.send_window_start && c?.send_window_end && <span>window <b>{c.send_window_start}–{c.send_window_end} UTC</b></span>}
        </div>
        <div className="psl-camp-progress"><b style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="psl-btnbar">
        <button className="psl-btn" disabled={busy} onClick={onRefresh}>Refresh</button>
        <button className="psl-btn" disabled={busy} onClick={onPause}>Pause</button>
        <button className="psl-btn" disabled={busy} onClick={onSetSending}>Set Sending</button>
        <button className="psl-btn" disabled={busy} onClick={onStartNow}>Start Now</button>
        <button className="psl-btn" disabled={busy} onClick={onSendTick} title="Trigger a send tick immediately">Send Tick Now</button>
        <div className="psl-fieldrow">
          <input type="datetime-local" className="psl-input" value={when} onChange={(e) => setWhen(e.target.value)} />
          <button className="psl-btn" disabled={busy || !when} onClick={() => onSchedule(when)}>Schedule</button>
        </div>
      </div>
    </div>
  );
}

function StepsEditor({ campaignId, templates }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  React.useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    setErr("");
    apiGetSteps(campaignId)
      .then((items) => {
        const norm = items.map((i) => ({
          step_no: i.step_no,
          template_id: i.template_id,
          offset_days: i.offset_days ?? (i.step_no === 1 ? 0 : 2),
          send_hour_utc: i.send_hour_utc ?? null,
          thread_with_first: !!i.thread_with_first,
        })).sort((a, b) => a.step_no - b.step_no);
        setRows(norm);
      })
      .catch((e) => setErr(e?.message || "Failed to load steps"))
      .finally(() => setLoading(false));
  }, [campaignId]);
  const addRow = () => {
    const step_no = rows.length ? Math.max(...rows.map((r) => r.step_no)) + 1 : 1;
    setRows([
      ...rows,
      { step_no, template_id: templates[0]?.id || null, offset_days: step_no === 1 ? 0 : 2, send_hour_utc: step_no === 1 ? null : 14, thread_with_first: true },
    ]);
  };
  const save = async () => {
    if (!rows.length) { alert("Add at least one step."); return; }
    if (rows.some((r) => !r.template_id)) { alert("Each step needs a template."); return; }
    const cleaned = rows.map((r) => ({
      step_no: Math.max(1, Number(r.step_no || 1)),
      template_id: Number(r.template_id),
      offset_days: Math.max(0, Number(r.offset_days || 0)),
      send_hour_utc: r.send_hour_utc === null || r.send_hour_utc === "" ? null : Math.min(23, Math.max(0, Number(r.send_hour_utc))),
      thread_with_first: !!r.thread_with_first,
    }));
    await apiSaveSteps(campaignId, cleaned);
    alert("Sequence saved ✓");
  };
  if (!campaignId) return null;
  return (
    <div className="psl-camp-card" style={{ marginBottom: 12 }}>
      <h4 style={{ margin: "0 0 8px" }}>Email Sequence</h4>
      {loading ? (
        <div style={{ color: "var(--psl-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ border: "1px dashed var(--psl-border)", borderRadius: 12, padding: 12, background: "#0f172a", color: "#cbd5e1" }}>
          No steps yet. Click <b>Add Step</b> to create your first message.
        </div>
      ) : (
        <div className="psl-camp-steps" style={{ marginBottom: 8 }}>
          {rows.map((r, idx) => (
            <div key={idx} className="psl-row">
              <input className="psl-input" type="number" min={1} value={r.step_no}
                onChange={(e) => setRows(rows.map((x) => (x === r ? { ...x, step_no: parseInt(e.target.value || "1", 10) } : x)))} title="Step number" />
              <select className="psl-input" value={r.template_id || ""}
                onChange={(e) => setRows(rows.map((x) => (x === r ? { ...x, template_id: parseInt(e.target.value, 10) } : x)))}>
                <option value="" disabled>Pick a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.subject}</option>)}
              </select>
              <input className="psl-input" type="number" min={0} value={r.offset_days}
                onChange={(e) => setRows(rows.map((x) => (x === r ? { ...x, offset_days: parseInt(e.target.value || "0", 10) } : x)))} title="Offset days" placeholder="Offset days" />
              <input className="psl-input" type="number" min={0} max={23} value={r.send_hour_utc ?? ""}
                onChange={(e) => {
                  const v = e.target.value === "" ? null : Math.min(23, Math.max(0, parseInt(e.target.value, 10)));
                  setRows(rows.map((x) => (x === r ? { ...x, send_hour_utc: v } : x)));
                }} placeholder="UTC hour (0–23)" title="UTC hour (0–23)" />
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={r.thread_with_first}
                  onChange={(e) => setRows(rows.map((x) => (x === r ? { ...x, thread_with_first: e.target.checked } : x)))} />
                Thread with first
              </label>
              <button className="psl-btn" onClick={() => setRows(rows.filter((x) => x !== r))}>Remove</button>
            </div>
          ))}
        </div>
      )}
      <div className="psl-actions">
        <button className="psl-btn" onClick={addRow}>Add Step</button>
        <button className="psl-btn primary" onClick={save}>Save Sequence</button>
      </div>
      {err && <div style={{ color: "#9b1c1c" }}>{err}</div>}
    </div>
  );
}

const SAFE_COUNTRY_GROUPS = typeof COUNTRY_GROUPS !== "undefined" ? COUNTRY_GROUPS : {};
const maskEmail = (email) => {
  if (!email) return "";
  const [user, domain] = email.split("@");
  if (!domain) return "••••••";
  const u = user.length === 0 ? "••" : user.length === 1 ? user[0] + "•" : user.length === 2 ? user : user.slice(0, 2) + "•••";
  const d = domain.split(".")[0];
  const tld = domain.slice(d.length);
  const dm = d.length === 0 ? "••" : d.length === 1 ? d[0] + "•" : d.length === 2 ? d : d.slice(0, 2) + "•••";
  return `${u}@${dm}${tld}`;
};
const maskHandle = (handle) => {
  if (!handle) return "";
  const h = handle.replace(/^@/, "");
  if (h.length === 0) return "@••";
  if (h.length === 1) return "@" + h[0] + "•";
  if (h.length === 2) return "@" + h;
  return "@" + h.slice(0, 2) + "•••";
};
const norm = (s) => (s || "").trim().toLowerCase();
const BASE_TAGS = ["Chill", "EDM", "Hip-Hop", "Lo-fi", "Indie", "Pop", "Trap", "House", "Jazz", "Acoustic", "Hip Hop"];
const BASE_NORM = new Set(BASE_TAGS.map(norm));
const buildTagPool = (customTagLabels) => {
  const base = BASE_TAGS.map((label) => ({ norm: norm(label), label }));
  const custom = Object.entries(customTagLabels).map(([n, label]) => ({ norm: n, label }));
  return [...base, ...custom];
};
const inferKeywordsForRow = (row, tagPool) => {
  const existing = Array.isArray(row.keywords) ? row.keywords : [];
  if (existing.length) return existing;
  const hay = [row.name || "", row.curator || "", row.description || ""].join(" ").toLowerCase();
  const hits = [];
  for (const t of tagPool) if (t.norm && hay.includes(t.norm)) hits.push(t.label);
  return Array.from(new Set(hits));
};
const nextPaint = () => typeof window === "undefined" ? Promise.resolve() : new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
const chunk = (arr, size) => { const out = []; for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size)); return out; };
function buildSeed() { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; }
function buildLiveBody(queryTerms, opts = {}) {
  const { contactMode = "any", requireContact = false, useContactPrefilter = true, includeFollowers = false, markets = [], targetResults = 0, offsetConcurrency = 24, offsetMode = "random", followersWorkers = 10 } = opts;
  const seed = buildSeed();
  return {
    query: Array.isArray(queryTerms) ? queryTerms : [queryTerms],
    markets,
    limit_per_market: 1000,
    market_concurrency: 24,
    offset_mode: offsetMode,
    use_contact_prefilter: useContactPrefilter,
    require_contact: requireContact,
    contact_mode: contactMode,
    query_expansion: true,
    include_followers: includeFollowers,
    offset_concurrency: offsetConcurrency,
    target_results: targetResults || 0,
    salt: `A-${seed}`,
    followers_workers: 24,
  };
}
function buildDbBody(query, markets, sortBy, sortDir) {
  return { query, markets, limit: 1000, offset: 0, sort: sortBy, direction: sortDir };
}
async function forceNextNowMany(ids, { batch = 25 } = {}) {
  for (const part of chunk(ids, batch)) await Promise.all(part.map(id => apiForceNextNow(id).catch(() => null)));
}
async function primeAllQueuedNow(campaignId) {
  const limit = 200;
  let offset = 0;
  while (true) {
    const r = await apiGetRecipients(campaignId, { stage: "queued", limit, offset });
    const ids = (r.items || []).map(x => x.id);
    if (!ids.length) break;
    await forceNextNowMany(ids);
    offset += limit;
    if (!r.total || offset >= r.total) break;
  }
}

export default function Search() {
  const seo = seoPresets["/search"];
  const { credits, spend, subscription, revealUsage, loading: creditsLoading, refresh } = useServerCredits();
  const canUseCampaigns = true; // All users can use campaigns now
  const canSearch = true; // All users can search
  const [showOutOfCredits, setShowOutOfCredits] = useState(false);
  const [showTutorial, setShowTutorial] = useState(() => {
    return localStorage.getItem('searchTutorialCompleted') !== 'true';
  });
  const revealed = useRevealedApi();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followersOverlay, setFollowersOverlay] = useState({});
  const [visibleIds, setVisibleIds] = useState(new Set());
  const visibleIdsRef = useRef(new Set());
  useEffect(() => { visibleIdsRef.current = visibleIds; }, [visibleIds]);
  const [qInput, setQInput] = useState("");
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [customTagLabels, setCustomTagLabels] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("live");
  const [source, setSource] = useState("live");
  const isRevealedTab = activeTab === "revealed";
  const [sortBy, setSortBy] = useState("followers");
  const [sortDir, setSortDir] = useState("desc");
  const [revealing, setRevealing] = useState(false);
  const [onlyEmail, setOnlyEmail] = useState(false);
  const [onlyIG, setOnlyIG] = useState(false);
  const [notice, setNotice] = useState("");
  const [searching, setSearching] = useState(false);
  const [stickyPhase, setStickyPhase] = useState("hidden");
  const [freeVisibleMode, setFreeVisibleMode] = useState("random");
  const [selectsAllFiltered, setSelectsAllFiltered] = useState(false);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignErr, setCampaignErr] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignDailyCap, setNewCampaignDailyCap] = useState("");
  const [campaignDetail, setCampaignDetail] = useState({ items: [], total: 0, stage: "", q: "", page: 0, pageSize: 50 });
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplErr, setTplErr] = useState("");
  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [newCampaignWinStart, setNewCampaignWinStart] = useState("");
  const [newCampaignWinEnd, setNewCampaignWinEnd] = useState("");
  const [newCampaignStartAt, setNewCampaignStartAt] = useState("");
  const [newCampaignTemplateId, setNewCampaignTemplateId] = useState(null);
  const [recSelectedIds, setRecSelectedIds] = useState(new Set());
  const [recBulkBusy, setRecBulkBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const qDebounced = useDebouncedValue(campaignDetail.q, 400);
  async function refreshTemplates() {
    await refreshTemplatesWithDefault({
      setTplLoading,
      setTplErr,
      setTemplates,
      newCampaignTemplateId,
      setNewCampaignTemplateId,
    });
  }
  useEffect(() => {
    if (canUseCampaigns) {
      refreshTemplates();
    }
  }, [canUseCampaigns]);
  async function refreshCampaigns() {
    setCampaignsLoading(true); setCampaignErr("");
    try { const items = await apiListCampaigns(); setCampaigns(items); }
    catch (e) { setCampaignErr(e?.message || "Failed to load campaigns"); }
    finally { setCampaignsLoading(false); }
  }
  async function addSelectedToCampaign(campaignId) {
    const sel = getSelectedRows();
    if (!sel.length) {
      alert("Select some playlists first.");
      return;
    }

    const rows = sel
      .filter((r) => r.email && r.email.trim())
      .map((r) => ({
        playlist_id: r.id,
        curator_name: r.curator || "",
        email: r.email,
        ig_handle: r.ig || "",
        meta: {
          id: r.id,
          // these two are what sendOne actually uses:
          playlist_name: r.name,
          playlist_url: r.url || playlistUrl(r.url || r.id),

          // nice-to-have extras, also supported by your backend:
          name: r.name,
          url: r.url || playlistUrl(r.url || r.id),
          followers: typeof r.followers === "number" ? r.followers : null,
          tags: Array.isArray(r.keywords) ? r.keywords : undefined,
        },
      }));

    if (!rows.length) {
      alert("Selected rows have no emails.");
      return;
    }

    try {
      await apiAddRecipients(campaignId, rows);
      showNotice?.(`Added ${rows.length} to Campaign #${campaignId} ✓`);
    } catch (e) {
      alert(e?.message || "Failed to add recipients");
    }
  }
  function openCampaigns() {
    setCampaignsOpen(true);
    refreshCampaigns();
    refreshTemplates();
    setCampaignDetail({ items: [], total: 0, stage: "", q: "", page: 0, pageSize: 50 });
    setSelectedCampaignId(null);
    setRecSelectedIds(new Set());
  }
  async function loadCampaignDetail(id, stage = campaignDetail.stage, q = campaignDetail.q, page = 0) {
    const limit = campaignDetail.pageSize; const offset = page * limit;
    try {
      const r = await apiGetRecipients(id, { stage, q, limit, offset });
      setCampaignDetail({ items: r.items || [], total: r.total || 0, stage, q, page, pageSize: limit });
      setRecSelectedIds(new Set());
    } catch (e) { alert(e?.message || "Failed to load recipients"); }
  }
  async function createCampaignInline() {
    if (!newCampaignName.trim()) { alert("Enter a campaign name"); return; }
    try {
      if (!templates.length) { alert("Create a template first (see Templates panel)."); return; }
      const templateId = newCampaignTemplateId || templates[0]?.id;
      if (!templateId) { alert("Pick a template."); return; }
      setCreatingCampaign(true);
      const dailyCap = newCampaignDailyCap.trim() === "" ? null : Number(newCampaignDailyCap);
      const id = await apiCreateCampaign({
        name: newCampaignName.trim(),
        template_id: templateId,
        start_at: newCampaignStartAt ? new Date(newCampaignStartAt).toISOString() : null,
        send_window_start: newCampaignWinStart || null,
        send_window_end: newCampaignWinEnd || null,
        daily_cap: Number.isFinite(dailyCap) ? dailyCap : null,
      });
      setNewCampaignName(""); setNewCampaignDailyCap(""); setNewCampaignWinStart(""); setNewCampaignWinEnd(""); setNewCampaignStartAt("");
      await refreshCampaigns(); showNotice?.(`Campaign #${id} created ✓`);
    } catch (e) { alert(e?.message || "Failed to create campaign"); }
    finally { setCreatingCampaign(false); }
  }
  const [pickerOpen, setPickerOpen] = useState(false);
  const isFreeTier = !isPaidPlan(subscription);
  const hasUnlimited = isPaidPlan(subscription) || credits === null;
  const capIfFree = (arr) => arr; // Remove free results cap
  useEffect(() => { visibleIdsRef.current = visibleIds; }, [visibleIds]);
  useEffect(() => {
    const freeLimitApplies = isFreeTier && activeTab !== "revealed" && (source === "live" || source === "db");
    if (!freeLimitApplies) return;
    setSelectedIds((prev) => { const next = new Set(); for (const id of prev) if (visibleIds.has(id)) next.add(id); return next; });
    setExcludedIds((prev) => { const next = new Set(); for (const id of prev) if (visibleIds.has(id)) next.add(id); return next; });
  }, [visibleIds, isFreeTier, activeTab, source]);
  const sanitizeForExport = (row) => ({ ...row, email: isContactVisible(row) ? row.email || "" : "", ig: isContactVisible(row) ? row.ig || "" : "" });
  function exportWithCap(items, filename) { if (!Array.isArray(items) || items.length === 0) return; const capped = capIfFree(items).map(sanitizeForExport); exportToCSV(capped, filename); }
  const exportVisible = () => { if (!rows.length) return; exportWithCap(rows, "visible_playlists.csv"); };
  const exportSelected = () => { const sel = getSelectedRows(); if (!sel.length) return; exportWithCap(sel, "selected_playlists.csv"); };
  const exportAllFilt = () => { if (!sorted.length) return; exportWithCap(sorted, "all_filtered_playlists.csv"); };
  const stoppedEarlyRef = useRef(false);
  const stopSearchingEarly = () => { if (stoppedEarlyRef.current) return; stoppedEarlyRef.current = true; setSearching(false); setLoading(false); };
  const followersInFlight = useRef(false);
  const mergeFollowers = (map) => {
    if (!map) return;
    setFollowersOverlay((prev) => ({ ...prev, ...map }));
    setData((prev) => prev.map((r) => ({ ...r, followers: map[r.id] !== undefined ? map[r.id] : (typeof r.followers === "number" ? r.followers : null) })));
  };
  const followersFetchedRef = useRef(new Set());
  const fullFollowersRunRef = useRef(false);
  const getFollowersForAll = async () => {
    if (fullFollowersRunRef.current) return;
    fullFollowersRunRef.current = true;
    while (followersInFlight.current) await nextPaint();
    const collectPending = () => data.filter((r) => getFollowersForRow(r) == null).map((r) => r.id).filter((id) => !followersFetchedRef.current.has(id));
    let pending = collectPending();
    if (!pending.length) { fullFollowersRunRef.current = false; return; }
    followersInFlight.current = true; setFollowersLoading(true);
    try {
      for (const group of chunk(pending, 100)) {
        const map = await fetchFollowersForIds(group, { maxWorkers: 4 });
        mergeFollowers(map);
        Object.keys(map).forEach((id) => followersFetchedRef.current.add(id));
        await nextPaint();
      }
      pending = collectPending();
      for (const id of pending) {
        try {
          const map = await fetchFollowersForIds([id], { maxWorkers: 1 });
          mergeFollowers(map);
          if (map[id] !== undefined) followersFetchedRef.current.add(id);
          await nextPaint();
        } catch { }
      }
      showNotice("Followers updated ✓");
    } catch (e) { alert(e?.message || "Failed to fetch followers."); }
    finally { setFollowersLoading(false); followersInFlight.current = false; fullFollowersRunRef.current = false; }
  };
  useEffect(() => {
    if (searching) { setStickyPhase("entering"); return; }
    if (stickyPhase !== "hidden") {
      setStickyPhase("leaving");
      const t = setTimeout(() => setStickyPhase("hidden"), 220);
      return () => clearTimeout(t);
    }
  }, [searching, stickyPhase]);
  const hideTimerRef = useRef(null);
  const abortRef = useRef(null);
  const tbodyRef = useRef(null);
  const masterChkRef = useRef(null);
  const waitForTablePaint = async (expectRows, token) => {
    if (typeof window === "undefined") return;
    const selector = expectRows ? "[data-row]" : "[data-empty]";
    for (let i = 0; i < 6; i++) {
      if (!abortRef.current || abortRef.current.token !== token) break;
      await nextPaint();
      const root = tbodyRef.current;
      if (root?.querySelector(selector)) break;
    }
  };
  const hasShownNoCreditsRef = useRef(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [ranSearch, setRanSearch] = useState(false);
  useEffect(() => { document.body.classList.toggle("psl-lock", filtersOpen); return () => document.body.classList.remove("psl-lock"); }, [filtersOpen]);
  useEffect(() => {
    if (creditsLoading) return;

    const isFree = subscription?.status === "free";

    const outOfReveals = isFree && (revealUsage?.remaining ?? 1) <= 0;
    const outOfSearches = !hasUnlimited && (credits ?? 0) <= 0;

    if (outOfReveals || outOfSearches) {
      if (activeTab !== "revealed") setActiveTab("revealed");

      if (!hasShownNoCreditsRef.current) {
        setShowOutOfCredits(true);
        hasShownNoCreditsRef.current = true;
      }
    } else {
      hasShownNoCreditsRef.current = false;
    }
  }, [creditsLoading, credits, revealUsage, subscription, activeTab, hasUnlimited]);


  useEffect(() => { if (activeTab === "live" || activeTab === "db") setSource(activeTab); }, [activeTab]);
  useEffect(() => {
    try { abortRef.current?.controller?.abort(); } catch { }
    if (activeTab === "revealed") return;
    setData([]); setSelectedIds(new Set()); setSelectsAllFiltered(false); setExcludedIds(new Set()); setCurrentPage(1); setError(""); setFollowersOverlay({});
  }, [source, activeTab]);
  useEffect(() => {
    // reset keyword/contact filters and selections when switching tabs
    setSelectedTags(new Set());
    setCustomTagLabels({});
    setOnlyEmail(false);
    setOnlyIG(false);
    setSelectedIds(new Set());
    setSelectsAllFiltered(false);
    setExcludedIds(new Set());
    setCurrentPage(1);
    setQInput("");
    setError("");
  }, [activeTab]);
  const showNotice = (text, ms = 1800) => {
    setNotice(text);
    clearTimeout(hideTimerRef.current);
    if (ms && ms > 0 && Number.isFinite(ms)) hideTimerRef.current = setTimeout(() => setNotice(""), ms);
  };
  const hideNotice = () => { clearTimeout(hideTimerRef.current); setNotice(""); };
  const addTag = (raw) => {
    const label = (raw || "").trim();
    if (!label) return;
    const n = norm(label);
    setSelectedTags((prev) => { if (prev.has(n)) return prev; const next = new Set(prev); next.add(n); return next; });
    if (!BASE_NORM.has(n)) setCustomTagLabels((prev) => ({ ...prev, [n]: label }));
  };
  const removeTag = (tNorm) => {
    setSelectedTags((prev) => { const next = new Set(prev); next.delete(tNorm); return next; });
    if (!BASE_NORM.has(tNorm)) setCustomTagLabels((prev) => { const { [tNorm]: _drop, ...rest } = prev; return rest; });
  };
  const toggleAllVisible = () => {
    const pageIds = selectableIdsOnPage;
    if (pageIds.length === 0) return;
    if (isAllMode) {
      setExcludedIds((prev) => { const n = new Set(prev); if (allSelectableVisibleSelected) pageIds.forEach((id) => n.add(id)); else pageIds.forEach((id) => n.delete(id)); return n; });
      return;
    }
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (allSelectableVisibleSelected) { pageIds.forEach((id) => n.delete(id)); return n; }
      for (const id of pageIds) {
        if (!n.has(id)) {
          n.add(id);
        }
      }
      return n;
    });
  };
  const clearAll = () => {
    setQInput(""); setSelectedTags(new Set()); setCustomTagLabels({}); setOnlyEmail(false); setOnlyIG(false); setCurrentPage(1); setSelectedIds(new Set()); setData([]); setError("");
  };
  const exportToCSV = (items, filename) => {
    const delimiter = ",";
    const header = ["Playlist Name", "Curator", "Keywords", "Followers", "URL", "Email", "IG"];
    const esc = (v) => { const s = String(v ?? ""); return /[\",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = items.map((r) => [esc(r.name), esc(r.curator), esc((r.keywords || []).join(", ")), esc(r.followers ?? 0), esc(r.url), esc(r.email), esc(r.ig)].join(delimiter));
    const csv = "\uFEFF" + [header.join(delimiter), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const searchLock = useRef(false);
  const onSearch = async () => {
    if (searchLock.current) return;
    searchLock.current = true;
    let gotAny = false;
    let controller;
    try {
      if (source !== "revealed" && !creditsLoading && !hasUnlimited && (credits ?? 0) <= 0) {
        setShowOutOfCredits(true);
        return;
      }

      const trimmed = qInput.trim();
      const nextSelectedTags = new Set(selectedTags);
      if (trimmed) {
        const n = norm(trimmed);
        nextSelectedTags.add(n);
        if (!BASE_NORM.has(n)) setCustomTagLabels((prev) => ({ ...prev, [n]: trimmed }));
        setQInput("");
        setSelectedTags(nextSelectedTags);
      }
      const query = Array.from(nextSelectedTags);
      if (query.length === 0) { setError("Type a keyword and press Enter, or add some tags, then Search."); return; }
      const contact_mode = onlyEmail && !onlyIG ? "email" : onlyIG && !onlyEmail ? "ig" : "any";
      try { abortRef.current?.controller?.abort(); } catch { }
      setError(""); setSelectedIds(new Set()); setSelectsAllFiltered(false); setExcludedIds(new Set()); setCurrentPage(1); setData([]); followersFetchedRef.current = new Set();
      const token = {}; abortRef.current = { token }; setRanSearch(true); stoppedEarlyRef.current = false; setLoading(true); hideNotice(); setSearching(true);
      try {
        // Searches are now unlimited for all users
        if (activeTab !== "revealed") {
          try {
            await apiFetch("/api/usage/searches/use", { method: "POST" });
            // Pull down updated remaining searches (credits)
            refresh?.();
          } catch (e) {
            if (e?.status === 401) {
              setError("Please sign in to search.");
              setSearching(false);
              setLoading(false);
              return;
            }
            // Limit reached → show the "free searches" modal
            if (e?.status === 402) {
              setShowOutOfCredits(true);
              setError(e?.message || "Search limit reached.");
              refresh?.();  // make sure credits drop to 0 in UI
              setSearching(false);
              setLoading(false);
              return;
            }

            setError("Could not verify search access. Please try again.");
            setSearching(false);
            setLoading(false);
            return;
          }
        }
        controller = new AbortController();
        abortRef.current = { token, controller };
        let endpoint, body;
        if (source === "db") { endpoint = "/database/search"; body = buildDbBody(query, [], sortBy, sortDir); }
        else { endpoint = "/api/search/search"; body = buildLiveBody(query, { contactMode: contact_mode, requireContact: true, useContactPrefilter: true, includeFollowers: true, followersWorkers: 10, targetResults: 800, offsetConcurrency: 36, offsetMode: "random" }); }
        const res = await fetch(playlistUrl(endpoint), { method: "POST", headers: { "Content-Type": "application/json", Accept: "text/x-ndjson, application/json" }, body: JSON.stringify(body), signal: controller.signal });
        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (!res.ok) {
          const json = await res.json().catch(() => ({ error: "unknown" }));
          if (json.error === "limit_reached") {
            setShowSearchLimit(true);
            setError(json.message || "Search limit reached");
            return;
          }
          const msg = json.message || json.error || await res.text().catch(() => "");
          throw new Error(msg || `HTTP ${res.status}`);
        }
        const applyItems = (items) => {
          const tagPool = buildTagPool(customTagLabels);
          const processed = items.map((r) => ({ ...r, keywords: inferKeywordsForRow(r, tagPool) }));
          flushSync(() => setData(processed));
        };
        const applyFollowers = (map) => {
          if (!map) return;
          if (Object.keys(map).length) requestAnimationFrame(() => mergeFollowers(map));
        };
        if (ct.includes("text/x-ndjson") && res.body && res.body.getReader) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = "";
          await waitForTablePaint(false, token);
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            let idx;
            while ((idx = buf.indexOf("\n")) >= 0) {
              const line = buf.slice(0, idx).trim();
              buf = buf.slice(idx + 1);
              if (!line || (line[0] !== "{" && line[0] !== "[")) continue;
              try {
                const obj = JSON.parse(line);
                if (obj.items) {
                  applyItems(obj.items);
                  if (obj.items.length) {
                    gotAny = true;
                    if (!stoppedEarlyRef.current) { await waitForTablePaint(true, token); stopSearchingEarly(); }
                  }
                }
                if (obj.followers) applyFollowers(obj.followers);
                await nextPaint();
              } catch { }
            }
          }
          const tail = buf.trim();
          if (tail && (tail[0] === "{" || tail[0] === "[")) {
            try {
              const obj = JSON.parse(tail);
              if (obj.items) { applyItems(obj.items); if (obj.items.length) gotAny = true; }
              if (obj.followers) applyFollowers(obj.followers);
            } catch { }
          }
        } else {
          let json;
          try { json = await res.json(); }
          catch { const txt = await res.text().catch(() => ""); throw new Error(txt || "Unexpected response"); }
          const items = Array.isArray(json.items) ? json.items : [];
          const followersMap = json.followers || {};
          const tagPool = buildTagPool(customTagLabels);
          const processed = items.map((r) => ({ ...r, keywords: inferKeywordsForRow(r, tagPool) }));
          flushSync(() => setData(processed));
          await waitForTablePaint(processed.length > 0, token);
          if (processed.length) stopSearchingEarly();
          if (Object.keys(followersMap).length) { await nextPaint(); mergeFollowers(followersMap); }
        }
        if (window.innerWidth <= 900 && gotAny) setFiltersOpen(false);
      } catch (e) {
        if (abortRef.current && abortRef.current.token !== token) return;
        const aborted = controller?.signal?.aborted || e?.name === "AbortError" || /abort/i.test(String(e?.message)) || String(e) === "TypeError: Failed to fetch";
        if (!aborted) { const msg = e?.message?.trim(); flushSync(() => setError(msg || "Search failed. Please try again.")); await waitForTablePaint(false, token); }
      } finally { abortRef.current = null; setLoading(false); setSearching(false); }
    } finally { searchLock.current = false; }
  };
  const isContactVisible = (row) => activeTab === "revealed" || revealed.isRevealed(row.id);
  const getFollowersForRow = (row) => { const v = followersOverlay[row.id] ?? row.followers; return typeof v === "number" && Number.isFinite(v) ? v : null; };
  const revealContactsFor = async (row) => {
    if (revealed.isRevealed(row.id)) return;
    if (!(row.email?.trim() || row.ig?.trim())) { alert("Nothing to reveal for this row."); return; }
    try {
      await spend(1);
      await revealed.add({ id: row.id, name: row.name, curator: row.curator, countries: row.countries || [], keywords: row.keywords || [], url: row.url, email: row.email || "", ig: row.ig || "", followers: getFollowersForRow(row) });
      showNotice("1 reveal used ✓");
    } catch (e) {
      const msg = e?.message || "";
      if (msg === "not_signed_in") alert("Please log in to use reveals.");
      else if (msg.includes("limit_reached")) alert("You've used all 10 free reveals this month. Upgrade to continue.");
      else if (msg.includes("insufficient")) alert("You're out of reveals. Go to Pricing to upgrade.");
      else alert(msg || "Could not use a reveal. Please try again.");
    }
  };
  const baseDataset = useMemo(() => (activeTab === "revealed" ? revealed.items : data), [activeTab, data, revealed.items]);
  const filtered = useMemo(() => {
    let arr = baseDataset;
    const hasEmail = (r) => !!(r.email && String(r.email).trim());
    const hasIG = (r) => !!(r.ig && String(r.ig).trim());

    // always require at least one contact in revealed
    if (activeTab === "revealed") {
      arr = arr.filter((r) => hasEmail(r) || hasIG(r));
    }

    if (onlyEmail && onlyIG) arr = arr.filter((r) => hasEmail(r) && hasIG(r));
    else if (onlyEmail) arr = arr.filter(hasEmail);
    else if (onlyIG) arr = arr.filter(hasIG);
    else if (activeTab !== "revealed") arr = arr.filter((r) => hasEmail(r) || hasIG(r));

    if (activeTab === "revealed" && selectedTags.size > 0) {
      const tags = Array.from(selectedTags);
      arr = arr.filter((r) => {
        const rowTags = (r.keywords || []).map(norm);
        return tags.every((t) => rowTags.includes(t));
      });
    }
    return arr;
  }, [baseDataset, onlyEmail, onlyIG, activeTab, selectedTags]);
  useEffect(() => {
    // Remove free limit - show all results
    const ids = filtered.map((r) => r.id);
    setVisibleIds(new Set(ids));
  }, [filtered.map((r) => r.id).join("|")]);
  const isAllMode = selectsAllFiltered;
  const isRowSelected = (id) => (isAllMode ? !excludedIds.has(id) : selectedIds.has(id));
  const selectedCount = useMemo(() => (isAllMode ? filtered.length - excludedIds.size : selectedIds.size), [isAllMode, filtered.length, excludedIds.size, selectedIds.size]);
  const excludedCount = useMemo(() => (isAllMode ? filtered.length - selectedCount : 0), [isAllMode, filtered.length, selectedCount]);
  const shouldBlurRow = (id) => false; // Remove row blurring for free users
  const getSelectedRows = () => {
    return isAllMode ? filtered.filter((r) => !excludedIds.has(r.id)) : baseDataset.filter((r) => selectedIds.has(r.id));
  };
  const selectedWithEmail = () => getSelectedRows().filter((r) => r.email && r.email.trim());
  // Helper counts for campaign button UX
  const selectedWithEmailRows = selectedWithEmail();
  const selectedWithEmailCount = selectedWithEmailRows.length;
  const disableAddToCampaign = selectedWithEmailCount === 0;

  const clearSelection = () => { setSelectsAllFiltered(false); setSelectedIds(new Set()); setExcludedIds(new Set()); };
  const revealableSelected = useMemo(() => {
    const sel = getSelectedRows();
    return sel.filter((r) => {
      if (revealed.isRevealed(r.id)) return false;
      const hasContact = (r.email && r.email.trim()) || (r.ig && r.ig.trim());
      return !!hasContact;
    });
  }, [filtered, baseDataset, selectedIds, excludedIds, selectsAllFiltered, revealed]);
  const revealableCount = revealableSelected.length;
  const getFollowersForVisible = async () => {
    if (!rows.length || followersInFlight.current || fullFollowersRunRef.current) return;
    setFollowersLoading(true); followersInFlight.current = true;
    try {
      const ids = rows.filter((r) => getFollowersForRow(r) == null).map((r) => r.id);
      if (!ids.length) return;
      const map = await fetchFollowersForIds(ids, { maxWorkers: 3, maxAgeHours: 24 * 7 });
      mergeFollowers(map);
      showNotice("Followers updated ✓");
    } finally { setFollowersLoading(false); followersInFlight.current = false; }
  };
  const revealSelected = async () => {
    if (revealing) return;
    if (revealableCount === 0) { alert("No selected rows to reveal."); return; }
    const maxReveal = Math.min(revealableCount, revealUsage?.remaining || 0);
    if (maxReveal === 0) { alert("No reveals remaining this month."); return; }
    if (!confirm(`Reveal ${maxReveal} selected item(s)? This uses ${maxReveal} reveal(s).`)) return;
    setRevealing(true);
    try {
      await spend(maxReveal);
      const toReveal = revealableSelected.slice(0, maxReveal);
      const results = await Promise.allSettled(
        toReveal.map((row) =>
          revealed.add({
            id: row.id,
            name: row.name,
            curator: row.curator,
            countries: row.countries || [],
            keywords: row.keywords || [],
            url: row.url,
            email: row.email || "",
            ig: row.ig || "",
            followers: getFollowersForRow(row),
          })
        )
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok > 0) showNotice(`${ok} reveal${ok > 1 ? "s" : ""} used ✓`);
      if (fail > 0) alert(`${fail} item${fail > 1 ? "s" : ""} failed to reveal.`);
    } catch (e) {
      const msg = e?.message || "";
      if (msg === "not_signed_in") alert("Please log in to use credits.");
      else if (msg.toLowerCase().includes("limit_reached")) alert("You've used all 10 free reveals this month. Upgrade to continue.");
      else if (msg.toLowerCase().includes("insufficient")) alert("You're out of reveals. Go to Pricing to upgrade.");
      else alert(msg || "Could not use reveals. Please try again.");
    } finally { setRevealing(false); }
  };
  const sorted = useMemo(() => {
    let arr = [...filtered];
    if (sortBy === "followers") {
      arr.sort((a, b) => {
        const av = Number(a.followers ?? 0);
        const bv = Number(b.followers ?? 0);
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [filtered, sortBy, sortDir]);
  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const start = (currentPage - 1) * pageSize;
  const rows = sorted.slice(start, start + pageSize);
  const selectableIdsOnPage = useMemo(() => rows.filter((r) => !shouldBlurRow(r.id)).map((r) => r.id), [rows, visibleIds, isFreeTier, activeTab, source]);
  useEffect(() => {
    if (!ranSearch || !rows.length) return;
    const needsFollowers = rows.some((r) => getFollowersForRow(r) == null);
    if (needsFollowers) getFollowersForVisible();
  }, [ranSearch, rows.map((r) => r.id).join("|"), followersOverlay]);
  const maskedEmail = (row) => (revealed.isRevealed(row.id) ? row.email || "" : maskEmail(row.email));
  const maskedIG = (row) => (revealed.isRevealed(row.id) ? row.ig || "" : maskHandle(row.ig));
  const allSelectableVisibleSelected = selectableIdsOnPage.length > 0 && selectableIdsOnPage.every((id) => isRowSelected(id));
  const someSelectableVisibleSelected = selectableIdsOnPage.some((id) => isRowSelected(id)) && !allSelectableVisibleSelected;
  useEffect(() => { if (masterChkRef.current) masterChkRef.current.indeterminate = someSelectableVisibleSelected; }, [someSelectableVisibleSelected]);
  useEffect(() => { if (!selectedCampaignId) return; loadCampaignDetail(selectedCampaignId, campaignDetail.stage, qDebounced, 0); }, [qDebounced]);
  const modeLabel = activeTab === "live" ? "Live" : activeTab === "db" ? "Database" : "Revealed";

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="psl-subnav">
        <div className="psl-brand" aria-label="Playlists Searcher">
          <span className="psl-dot" />
          <span>Playlists</span>
        </div>
        {subscription?.status === "free" && (
          <div className="psl-credits-display" style={{
            fontSize: '13px',
            color: '#6b7280',
            background: '#f3f4f6',
            padding: '4px 8px',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            {revealUsage.remaining || 0} reveals remaining this month
          </div>
        )}
        <div className="psl-tabs" role="tablist" aria-label="Search modes">
          <span className="psl-tabwrap">
            <button
              className={`psl-tab ${activeTab === "live" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "live"}
              onClick={() => setActiveTab("live")}
              title="Live Spotify crawler"
            >
              Live Search
              <InfoDot><h6>Live Search</h6><p>Crawls Spotify in real time globally.</p><p>Freshest results. May be slower.</p></InfoDot>
            </button>
          </span>
          <span className="psl-tabwrap" style={{ marginLeft: 10 }}>
            <button
              className={`psl-tab ${activeTab === "db" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "db"}
              onClick={() => setActiveTab("db")}
              title="Database (faster)"
            >
              Database Search
              <InfoDot><h6>Database Search</h6><p>Queries cached results for near-instant speed.</p><p>Great for quick scans. May be less fresh.</p></InfoDot>
            </button>
          </span>
          <span className="psl-tabwrap" style={{ marginLeft: 10 }}>
            <button className={`psl-tab ${activeTab === "revealed" ? "active" : ""}`} role="tab" aria-selected={activeTab === "revealed"} onClick={() => setActiveTab("revealed")} title="Your revealed contacts">
              Revealed <span style={{ marginLeft: 6, opacity: 0.8 }}>({revealed.items.length})</span>
            </button>
          </span>
        </div>
        <button
          className="psl-btn psl-filters-toggle"
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          aria-controls="psl-filters"
        >
          Search terms
        </button>
      </div>

      <div className="psl-shell">
        <div className="psl-grid">
          {filtersOpen && (
            <button
              className="psl-overlay"
              aria-label="Close search terms"
              onClick={() => setFiltersOpen(false)}
            />
          )}
          <aside
            id="psl-filters"
            className={`psl-side ${filtersOpen ? "is-open" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="Search terms"
          >
            <div className="psl-drawer-head">
              <h4>Search terms</h4>
              <button
                type="button"
                className="psl-btn psl-drawer-close"
                onClick={() => setFiltersOpen(false)}
                aria-label="Close search terms"
              >
                ✕
              </button>
            </div>
            <label className="psl-search" aria-label="Detailed search">
              <SearchIcon />
              <input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (qInput.trim()) { addTag(qInput); setQInput(""); }
                    else if (activeTab !== "revealed") { onSearch(); }
                    if (window.innerWidth <= 900 && rows.length > 0) setFiltersOpen(false);
                  }
                }}
                placeholder="Type a keyword (e.g., edm) and press Enter…"
              />
            </label>
            <div className="psl-group">
              <h4>Keywords</h4>
              <div className="psl-pills">
                {BASE_TAGS.map((t) => {
                  const isOn = selectedTags.has(norm(t));
                  return (
                    <button key={t} className={"psl-pill " + (isOn ? "selected" : "")}
                      onClick={() => {
                        const tt = norm(t);
                        setSelectedTags((prev) => { const next = new Set(prev); next.has(tt) ? next.delete(tt) : next.add(tt); return next; });
                      }}>
                      {t}
                    </button>
                  );
                })}
                {Array.from(selectedTags).filter((nTag) => !BASE_NORM.has(nTag)).map((nTag) => (
                  <button key={"custom_" + nTag} className="psl-pill selected" onClick={() => removeTag(nTag)} title="Remove keyword">
                    {customTagLabels[nTag] || nTag}<span style={{ marginLeft: 6, opacity: 0.85 }}>×</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="psl-group psl-last">
              <h4>Contacts</h4>
              <div className="psl-pills">
                <button className={"psl-pill " + (onlyEmail ? "selected" : "")} onClick={() => { setOnlyEmail((v) => !v); setCurrentPage(1); }}>Email</button>
                <button className={"psl-pill " + (onlyIG ? "selected" : "")} onClick={() => { setOnlyIG((v) => !v); setCurrentPage(1); }}>IG</button>
              </div>
              <p style={{ fontSize: 12, color: "#6b7280", margin: "6px 0 0" }}>Tip: turn both on to require Email + IG.</p>
            </div>
            <div className="psl-actions">
              <button className="psl-btn" onClick={() => { clearAll(); if (window.innerWidth <= 900) setFiltersOpen(false); }}>Clear</button>
              {activeTab !== "revealed" && (
                <button
                  className="psl-btn primary"
                  onClick={() => {
                    onSearch();
                    setFiltersOpen(false);
                  }}
                  disabled={
                    searching ||
                    (selectedTags.size === 0 && !qInput.trim())
                  }
                >
                  {loading ? "Searching…" : "Search"}
                </button>
              )}

            </div>
            {error && <div style={{ color: "#9b1c1c", margin: "8px" }}>{error}</div>}
            {activeTab === "revealed" && revealed.err && <div style={{ color: "#9b1c1c", margin: "8px" }}>{revealed.err}</div>}
          </aside>

          <main className="psl-main">
            <div className="psl-toolbar">
              <div className="psl-count psl-count--with-selection">
                <span className="psl-inline">{modeLabel} • {sorted.length} result{sorted.length === 1 ? "" : "s"}</span>
                <span className="psl-inline">/ {selectedCount} selected</span>
                {!isAllMode && filtered.length > 0 && (
                  <>
                    <span className="psl-sep" aria-hidden>•</span>
                    {allSelectableVisibleSelected && filtered.length > rows.length ? (
                      <button type="button" className="psl-btn" onClick={() => setSelectsAllFiltered(true)} aria-label={`Select all ${filtered.length} results`}>
                        Select all {filtered.length} results
                      </button>
                    ) : (
                      <button type="button" className="psl-btn" onClick={() => setSelectsAllFiltered(true)} aria-label="Select all results">
                        Select all results
                      </button>
                    )}
                  </>
                )}
                {selectedCount > 0 && (
                  <>
                    {isAllMode && (
                      <>
                        <span className="psl-sep" aria-hidden>•</span>
                        {excludedCount === 0 ? (
                          <span className="psl-inline" style={{ opacity: 0.75 }}>(All results selected)</span>
                        ) : (
                          <>
                            <span className="psl-inline" style={{ opacity: 0.75 }}>(All results selected, {excludedCount} excluded)</span>
                            <span className="psl-sep" aria-hidden>•</span>
                            <button type="button" className="psl-linkbtn" onClick={() => setExcludedIds(new Set())} aria-label="Include excluded items again">
                              Re-select all
                            </button>
                          </>
                        )}
                      </>
                    )}
                    <span className="psl-sep" aria-hidden>•</span>
                    {isAllMode ? (
                      <button type="button" className="psl-btn danger" onClick={clearSelection} aria-label="Clear selection" title="Clear all selected results">Clear</button>
                    ) : (
                      <button type="button" className="psl-linkbtn" onClick={clearSelection} aria-label="Clear selection">Clear</button>
                    )}
                  </>
                )}
              </div>
              <div className="psl-actions-row">
                {activeTab === "revealed" ? (
                  canUseCampaigns ? (
                    <>
                      {disableAddToCampaign && selectedCount > 0 && (
                        <span className="psl-campaign-hint">
                          Only contacts with an email can be added.
                        </span>
                      )}
                      <button
                        className="psl-btn"
                        onClick={() => setPickerOpen(true)}
                        disabled={disableAddToCampaign}
                        title={
                          disableAddToCampaign
                            ? selectedCount > 0
                              ? "Only contacts with an email address can be added to a campaign."
                              : "Select contacts with an email address to add to a campaign."
                            : "Add selected revealed contacts to an email campaign"
                        }
                      >
                        Add Selected to Campaign
                      </button>
                    </>
                  ) : null
                ) : (
                  <>
                    <button
                      className="psl-btn"
                      onClick={revealSelected}
                      disabled={revealing || revealableCount === 0}
                      title={
                        revealableCount > 0
                          ? `Reveal ${revealableCount} selected (uses ${revealableCount} reveals)`
                          : "Select rows with contact info to reveal"
                      }
                    >
                      {revealing
                        ? "Revealing…"
                        : `Reveal Selected${revealableCount > 0 ? ` (−${revealableCount})` : ""
                        }`}
                    </button>
                    <button
                      className="psl-btn"
                      onClick={exportVisible}
                      disabled={rows.length === 0}
                    >
                      Export Visible
                    </button>
                    <button
                      className="psl-btn"
                      onClick={exportAllFilt}
                      disabled={sorted.length === 0}
                    >
                      Export All
                    </button>
                    <button
                      className="psl-btn primary"
                      onClick={exportSelected}
                      disabled={selectedCount === 0}
                    >
                      Export Selected
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="psl-tableblock">
              <div className="psl-tablewrap">
                <table className="psl-table">
                  <thead>
                    <tr>
                      <th style={{ width: "4%" }}>
                        <input ref={masterChkRef} className="psl-chk" type="checkbox" checked={allSelectableVisibleSelected} onChange={toggleAllVisible} aria-label="Select all on this page" />
                      </th>
                      <th style={{ width: "22%" }}>Playlist Name</th>
                      <th style={{ width: "16%" }}>Curator</th>
                      <th style={{ width: "16%" }}>Keywords</th>
                      <th style={{ width: "10%" }} scope="col">
                        <button type="button" className={`psl-th-sortable ${sortBy === "followers" ? "active" : ""}`}
                          aria-sort={sortBy === "followers" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                          onClick={() => { setSortBy("followers"); setSortDir((d) => (sortBy === "followers" ? (d === "asc" ? "desc" : "asc") : "desc")); }}>
                          Followers {sortBy === "followers" && <span className="psl-sort-caret">{sortDir === "asc" ? "▲" : "▼"}</span>}
                        </button>
                      </th>
                      <th style={{ width: "6%" }}>URL</th>
                      <th style={{ width: "16%" }}>Email</th>
                      <th style={{ width: "6%" }}>IG</th>
                      {!isRevealedTab && <th style={{ width: "10%" }}>Reveal</th>}
                    </tr>
                  </thead>
                  <tbody ref={tbodyRef}>
                    {rows.length === 0 ? (
                      <tr data-empty>
                        <td
                          colSpan={isRevealedTab ? 8 : 9}
                          style={{ padding: 16, color: "var(--psl-muted)", textAlign: "center" }}
                        >
                          {ranSearch ? "No matches." : "No results yet."}
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => {
                        const blur = shouldBlurRow(r.id);
                        const hasContact = r.email?.trim() || r.ig?.trim();
                        const placeholder = (len = 6) => "•".repeat(len);
                        const preview = (s, n = 3) => (s ? s.slice(0, n) + "…" : "…");
                        const rowSelected = isRowSelected(r.id);
                        const rowClass = `${rowSelected ? "is-selected " : ""}${blur ? "is-blurred" : ""}`;
                        return (
                          <tr data-row key={r.id} className={rowClass}>
                            <td>
                              <input className="psl-chk" type="checkbox" checked={rowSelected} disabled={blur}
                                onChange={() => {
                                  if (blur) return;
                                  if (isAllMode) setExcludedIds((prev) => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; });
                                  else setSelectedIds((prev) => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; });
                                }}
                                aria-label={`Select ${r.name || "row"}`}
                              />
                            </td>
                            <td className="psl-col-name" title={blur ? undefined : r.name}>{blur ? <span>{preview(r.name, 4)}</span> : <span>{r.name}</span>}</td>
                            <td>{blur ? <span>{preview(r.curator, 3)}</span> : <span>{r.curator}</span>}</td>
                            <td className="psl-col-keywords">{blur ? <span>{placeholder(5)}</span> : <span>{(r.keywords || []).join(", ")}</span>}</td>
                            <td>
                              {(() => {
                                const followerVal = getFollowersForRow(r);
                                if (blur) return <span style={{ color: "var(--psl-muted)" }}>—</span>;
                                if (activeTab === "live" && followerVal == null) return <span className="psl-mini-loader">loading…</span>;
                                if (followerVal == null) return <span style={{ color: "var(--psl-muted)" }}>—</span>;
                                return <span>{Number(followerVal).toLocaleString()}</span>;
                              })()}
                            </td>
                            <td>
                              {r.url ? (
                                (activeTab === "revealed" || revealed.isRevealed(r.id)) ? (
                                  <a className="psl-url" href={r.url} target="_blank" rel="noreferrer">Link</a>
                                ) : (
                                  <span style={{ color: "var(--psl-muted)" }}>Reveal to see</span>
                                )
                              ) : (
                                <span style={{ color: "var(--psl-muted)" }}>—</span>
                              )}
                            </td>
                            <td className="psl-col-email">
                              {r.email ? (
                                activeTab === "revealed" || revealed.isRevealed(r.id) ? (
                                  blur ? <span className="psl-blurcell">{r.email}</span> : <a className="psl-url" href={`mailto:${r.email}`}>{r.email}</a>
                                ) : blur ? <span className="psl-blurcell">••••••</span> : <span>{maskedEmail(r)}</span>
                              ) : <span style={{ color: "var(--psl-muted)" }}>—</span>}
                            </td>
                            <td>
                              {r.ig ? (
                                activeTab === "revealed" || revealed.isRevealed(r.id) ? (
                                  blur ? <span className="psl-blurcell">{r.ig}</span> : <a className="psl-url" href={`https://instagram.com/${r.ig.replace(/^@/, "")}`} target="_blank" rel="noreferrer">{r.ig}</a>
                                ) : blur ? <span className="psl-blurcell">@•••</span> : <span>{maskedIG(r)}</span>
                              ) : <span style={{ color: "var(--psl-muted)" }}>—</span>}
                            </td>
                            {!isRevealedTab && (
                              <td>
                                {blur || !hasContact ? (
                                  <span style={{ color: "var(--psl-muted)" }}></span>
                                ) : (
                                  <button
                                    className="psl-btn"
                                    disabled={revealed.isRevealed(r.id)}
                                    onClick={() => revealContactsFor(r)}
                                    title={
                                      revealed.isRevealed(r.id)
                                        ? "Already revealed"
                                        : "Click to reveal"
                                    }
                                  >
                                    {revealed.isRevealed(r.id) ? "Revealed" : "Reveal"}
                                  </button>
                                )}
                              </td>
                            )}

                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="psl-pager">
                <div className="psl-pageinfo">Showing {filtered.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, filtered.length)} of {filtered.length}</div>
                <div className="psl-pagectrl">
                  <button aria-label="Prev" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>←</button>
                  <span>Page</span>
                  <select value={currentPage} onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <span>of {totalPages}</span>
                  <button aria-label="Next" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>→</button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {stickyPhase !== "hidden" && <div className={`psl-notice psl-notice--sticky ${stickyPhase === "leaving" ? "is-leaving" : "is-entering"}`} role="status" aria-live="polite"><div>Searching…</div></div>}
      {!searching && notice && <div className="psl-notice" role="status" aria-live="polite"><div>{notice}</div></div>}




      {campaignsOpen && (
        <div className="psl-modal-overlay" role="dialog" aria-modal="true" aria-label="Campaigns">
          <div className="psl-modal psl-modal--campaigns">
            <div className="psl-modal-top">
              <h3>Campaigns</h3>
              <button className="psl-btn" onClick={() => setCampaignsOpen(false)}>Close</button>
            </div>

            <div className="psl-camp-card psl-camp-create-wrap">
              <h4 style={{ margin: "0 0 8px" }}>Create Campaign</h4>
              <div className="psl-camp-create">
                <input className="psl-input" placeholder="Campaign name" value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value)} />
                <select
                  className="psl-input"
                  value={newCampaignTemplateId || ""}
                  onChange={(e) => setNewCampaignTemplateId(Number(e.target.value))}
                >
                  <option value="" disabled>
                    {tplLoading ? "Loading templates…" : templates.length ? "Pick default template…" : "Create a template first"}
                  </option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} — {t.subject}</option>
                  ))}
                </select>
                <input className="psl-input" placeholder="Daily cap (default 200)" value={newCampaignDailyCap} onChange={(e) => setNewCampaignDailyCap(e.target.value.replace(/[^\d]/g, ""))} />
                <input className="psl-input" placeholder="Send window start (HH:MM)" value={newCampaignWinStart} onChange={(e) => setNewCampaignWinStart(e.target.value)} />
                <input className="psl-input" placeholder="Send window end (HH:MM)" value={newCampaignWinEnd} onChange={(e) => setNewCampaignWinEnd(e.target.value)} />
              </div>
              <div className="psl-camp-start">
                <input className="psl-input" type="datetime-local" placeholder="Start at (optional)" value={newCampaignStartAt} onChange={(e) => setNewCampaignStartAt(e.target.value)} />
                <button className="psl-btn primary" onClick={createCampaignInline} disabled={creatingCampaign}>{creatingCampaign ? "Creating…" : "Create"}</button>
              </div>
            </div>

            <div className="psl-camp-two">
              <div className="psl-camp-card">
                <div className="psl-flex-between">
                  <h4 style={{ margin: 0 }}>Your Campaigns</h4>
                  <button className="psl-btn" onClick={refreshCampaigns} disabled={campaignsLoading}>{campaignsLoading ? "Loading…" : "Refresh"}</button>
                </div>
                {campaignErr && <div style={{ color: "#9b1c1c", marginTop: 8 }}>{campaignErr}</div>}
                <div className="psl-camp-list">
                  {campaignsLoading ? (
                    <div style={{ color: "var(--psl-muted)" }}>Loading…</div>
                  ) : campaigns.length === 0 ? (
                    <div style={{ color: "var(--psl-muted)" }}>No campaigns yet.</div>
                  ) : (
                    campaigns.map((c) => (
                      <button key={c.id} type="button" className={"psl-camp-item " + (selectedCampaignId === c.id ? "is-active" : "")}
                        onClick={() => { setSelectedCampaignId(c.id); loadCampaignDetail(c.id, "", "", 0); }}>
                        <h5>{c.name} <span style={{ opacity: 0.7 }}>#{c.id}</span></h5>
                        <p>status: {c.status} • queued {c.queued} • emailed {c.emailed} • replied {c.replied}</p>
                      </button>
                    ))
                  )}
                </div>

                <div style={{ borderTop: "1px dashed var(--psl-border)", marginTop: 12, paddingTop: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                    <h4 style={{ margin: 0 }}>Templates</h4>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="psl-btn" onClick={() => setTplModalOpen(true)}>New Template</button>
                      <button className="psl-btn" onClick={refreshTemplates} disabled={tplLoading}>{tplLoading ? "Loading…" : "Refresh"}</button>
                    </div>
                  </div>
                  <div style={{ border: "1px dashed var(--psl-border)", borderRadius: 8, overflow: "auto" }}>
                    {tplLoading ? (
                      <div style={{ padding: 8, color: "var(--psl-muted)" }}>Loading…</div>
                    ) : tplErr ? (
                      <div style={{ padding: 8, color: "#9b1c1c" }}>{tplErr}</div>
                    ) : templates.length === 0 ? (
                      <div style={{ padding: 8, color: "var(--psl-muted)" }}>No templates yet.</div>
                    ) : (
                      <table className="psl-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Subject</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.map((t) => (
                            <tr key={t.id}>
                              <td>{t.name}</td>
                              <td>{t.subject}</td>
                              <td>
                                <button
                                  className="psl-btn"
                                  onClick={() => setTplModalOpen(true)}
                                  title="Edit in modal"
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              <div className="psl-camp-card">
                {!selectedCampaignId ? (
                  <div style={{ color: "var(--psl-muted)" }}>Select a campaign to edit its sequence and recipients.</div>
                ) : (
                  <>
                    <CampaignHeader
                      c={campaigns.find((x) => x.id === selectedCampaignId) || {}}
                      busy={statusBusy}
                      onRefresh={async () => { await refreshCampaigns(); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); }}
                      onStartNow={async () => {
                        try {
                          setStatusBusy(true);
                          await apiSetCampaignStatus(selectedCampaignId, "sending");
                          await apiScheduleCampaign(selectedCampaignId, null);
                          await primeAllQueuedNow(selectedCampaignId);
                          const r = await apiSendTick();
                          showNotice(`Started and sent ${r?.sent ?? 0} ✓`);
                          await refreshCampaigns();
                          await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page);
                        } finally { setStatusBusy(false); }
                      }}
                      onPause={async () => { try { setStatusBusy(true); await apiSetCampaignStatus(selectedCampaignId, "paused"); await refreshCampaigns(); showNotice("Paused ✓"); } finally { setStatusBusy(false); } }}
                      onSetSending={async () => { try { setStatusBusy(true); await apiSetCampaignStatus(selectedCampaignId, "sending"); await refreshCampaigns(); showNotice("Status set to sending ✓"); } finally { setStatusBusy(false); } }}
                      onSendTick={async () => { try { setStatusBusy(true); const r = await apiSendTick(); showNotice(`Send tick OK — sent ${r?.sent ?? 0}`); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); } finally { setStatusBusy(false); } }}
                      onSchedule={async (iso) => { try { setStatusBusy(true); await apiScheduleCampaign(selectedCampaignId, iso ? new Date(iso).toISOString() : null); await refreshCampaigns(); showNotice("Schedule updated ✓"); } finally { setStatusBusy(false); } }}
                    />
                    <StepsEditor campaignId={selectedCampaignId} templates={templates} />
                    <div>
                      <div className="psl-filterbar">
                        <select className="psl-input" value={campaignDetail.stage} onChange={(e) => loadCampaignDetail(selectedCampaignId, e.target.value, campaignDetail.q, 0)}>
                          <option value="">All stages</option>
                          <option value="queued">Queued</option>
                          <option value="emailed">Emailed</option>
                          <option value="bounced">Bounced</option>
                          <option value="replied">Replied</option>
                          <option value="negotiating">Negotiating</option>
                          <option value="confirmed">Confirmed</option>
                        </select>
                        <input className="psl-input" placeholder="Filter by email/curator…" value={campaignDetail.q} onChange={(e) => setCampaignDetail((d) => ({ ...d, q: e.target.value }))} />
                        <button className="psl-btn" onClick={() => loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, 0)}>Apply</button>
                      </div>

                      <div className="psl-camp-recips">
                        <table className="psl-table">
                          <thead>
                            <tr>
                              <th style={{ width: "5%" }}>
                                <input className="psl-chk" type="checkbox"
                                  checked={campaignDetail.items.length > 0 && campaignDetail.items.every((r) => recSelectedIds.has(r.id))}
                                  onChange={(e) => { if (e.target.checked) setRecSelectedIds(new Set(campaignDetail.items.map((x) => x.id))); else setRecSelectedIds(new Set()); }} />
                              </th>
                              <th style={{ width: "30%" }}>Email</th>
                              <th style={{ width: "20%" }}>Curator</th>
                              <th style={{ width: "12%" }}>Stage</th>
                              <th style={{ width: "23%" }}>Timestamps</th>
                              <th style={{ width: "10%" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaignDetail.items.length === 0 ? (
                              <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--psl-muted)" }}>No recipients.</td></tr>
                            ) : (
                              campaignDetail.items.map((r) => (
                                <tr key={r.id}>
                                  <td>
                                    <input className="psl-chk" type="checkbox" checked={recSelectedIds.has(r.id)}
                                      onChange={() => setRecSelectedIds((prev) => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} />
                                  </td>
                                  <td>{r.email}</td>
                                  <td>{r.curator_name || "—"}</td>
                                  <td><span className={`psl-chip psl-chip--${(r.stage || "").toLowerCase()}`}>{r.stage}</span></td>
                                  <td>
                                    {r.sent_at ? `sent ${new Date(r.sent_at).toLocaleString()}` : "—"}
                                    {r.last_event_at ? ` • last ${new Date(r.last_event_at).toLocaleString()}` : ""}
                                  </td>
                                  <td>
                                    <button className="psl-btn psl-btn--micro" onClick={async () => {
                                      try {
                                        await apiForceNextNow(r.id);
                                        showNotice("Next step scheduled for now ✓");
                                        await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page);
                                      } catch (e) { alert(e?.message || "Failed to reschedule"); }
                                    }}>
                                      Force Next Now
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <div> Total: {campaignDetail.total} </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <button className="psl-btn" disabled={campaignDetail.page === 0}
                            onClick={() => loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page - 1)}>Prev</button>
                          <span>Page {campaignDetail.page + 1}</span>
                          <button className="psl-btn" disabled={(campaignDetail.page + 1) * campaignDetail.pageSize >= campaignDetail.total}
                            onClick={() => loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page + 1)}>Next</button>
                        </div>
                      </div>

                      <div className="psl-camp-bulk">
                        <span style={{ color: "var(--psl-muted)" }}>Bulk:</span>
                        <button className="psl-btn" disabled={recSelectedIds.size === 0 || recBulkBusy}
                          onClick={async () => { try { setRecBulkBusy(true); await apiBulkStage(Array.from(recSelectedIds), "replied"); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); setRecSelectedIds(new Set()); showNotice("Marked selected → replied ✓"); } finally { setRecBulkBusy(false); } }}>
                          Mark selected → Replied
                        </button>
                        <button className="psl-btn" disabled={recSelectedIds.size === 0 || recBulkBusy}
                          onClick={async () => { try { setRecBulkBusy(true); await apiBulkStage(Array.from(recSelectedIds), "queued"); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); setRecSelectedIds(new Set()); showNotice("Marked selected → queued ✓"); } finally { setRecBulkBusy(false); } }}>
                          Back to queued
                        </button>
                        <button className="psl-btn" disabled={recSelectedIds.size === 0 || recBulkBusy}
                          onClick={async () => { try { setRecBulkBusy(true); await apiBulkStage(Array.from(recSelectedIds), "bounced"); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); setRecSelectedIds(new Set()); showNotice("Marked selected → bounced ✓"); } finally { setRecBulkBusy(false); } }}>
                          Mark selected → Bounced
                        </button>
                        <button className="psl-btn" disabled={recSelectedIds.size === 0 || recBulkBusy}
                          onClick={async () => { try { setRecBulkBusy(true); await forceNextNowMany(Array.from(recSelectedIds)); await apiSendTick(); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); setRecSelectedIds(new Set()); showNotice("Forced selected → Next now ✓"); } finally { setRecBulkBusy(false); } }}>
                          Force selected → Next now
                        </button>
                        <button className="psl-btn" disabled={campaignDetail.items.length === 0 || recBulkBusy}
                          onClick={async () => { try { setRecBulkBusy(true); const ids = campaignDetail.items.map(x => x.id); await forceNextNowMany(ids); await apiSendTick(); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); showNotice("Forced current page → Next now ✓"); } finally { setRecBulkBusy(false); } }}>
                          Force current page → Next now
                        </button>
                        <div style={{ marginLeft: "auto" }}>
                          <button className="psl-btn"
                            onClick={async () => { if (!campaignDetail.items.length) return; const ids = campaignDetail.items.map((x) => x.id); await apiBulkStage(ids, "replied"); await loadCampaignDetail(selectedCampaignId, campaignDetail.stage, campaignDetail.q, campaignDetail.page); showNotice("Marked current page as replied ✓"); }}>
                            Mark current page → Replied
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )
      }
      <CampaignPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        apiFetch={apiFetch}
        selectedRows={selectedWithEmail()}
        onAdded={({ campaignId, addedCount }) => { showNotice?.(`Added ${addedCount} contact${addedCount > 1 ? "s" : ""} to #${campaignId} ✓`); setPickerOpen(false); }}
      />
      {tplModalOpen && (
        <TemplateEditorModal
          open={tplModalOpen}
          onClose={() => setTplModalOpen(false)}
          templates={templates}
          tplLoading={tplLoading}
          tplErr={tplErr}
          onRefresh={refreshTemplates}
          onSaved={async () => {
            await refreshTemplates();
            setTplModalOpen(false);
          }}
          sampleRow={
            selectedWithEmail()[0] ||
            revealed.items[0] ||
            rows[0] || {
              name: "Playlist Name",
              url: "https://open.spotify.com/playlist/xyz",
              curator: "Curator",
              email: "curator@example.com",
            }
          }
        />
      )}

      {showTutorial && (
        <SearchTutorial
          onClose={() => setShowTutorial(false)}
          activeTab={activeTab}
          context={{
            selectedTags: selectedTags,
            qInput: qInput,
            searching: searching,
            rows: rows,
            filtersOpen: filtersOpen
          }}
        />
      )}

      <OutOfCreditsModal
        open={showOutOfCredits}
        onClose={() => setShowOutOfCredits(false)}
      />
    </>
  );
}
